import { useState } from 'react'
import { API_BASE } from '../lib/api'

export default function AdminRestaurantPanel({ adminToken }) {
	const [form, setForm] = useState({
		name: '',
		city: '',
		area: '',
		image: '',
		images: '',
		description: '',
		address: '',
		latitude: '',
		longitude: '',
		phone: '',
		cuisine: '',
		tags: '',
		highlights: '',
		priceLevel: 'Mid',
		openingHours: '',
		openingStatus: '',
		isOpen: true,
		delivery: false,
		takeaway: false,
		parking: false,
		distanceKm: 0,
	})
	const [uploading, setUploading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [status, setStatus] = useState('')

	function updateField(key, value) {
		setForm(prev => ({ ...prev, [key]: value }))
	}

	async function handleImageUpload(e) {
		const file = e.target.files?.[0]
		if (!file) return

		setUploading(true)
		setStatus('')
		try {
			const formData = new FormData()
			formData.append('image', file)
			const res = await fetch(`${API_BASE}/admin/upload-restaurant-image`, {
				method: 'POST',
				headers: { Authorization: `Bearer ${adminToken}` },
				body: formData,
			})
			const data = await res.json()
			if (!res.ok) throw new Error(data.message || 'Restaurant image upload failed.')
			updateField('image', data.url)
			setStatus('Restaurant image uploaded successfully.')
		} catch (err) {
			setStatus(err.message)
		} finally {
			setUploading(false)
			e.target.value = ''
		}
	}

	async function handleSubmit(e) {
		e.preventDefault()
		setSaving(true)
		setStatus('')

		try {
			const res = await fetch(`${API_BASE}/admin/restaurants`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${adminToken}`,
				},
				body: JSON.stringify({
					...form,
					distanceKm: Number(form.distanceKm) || 0,
					latitude: form.latitude === '' ? null : Number(form.latitude),
					longitude: form.longitude === '' ? null : Number(form.longitude),
				}),
			})
			const data = await res.json()
			if (!res.ok) throw new Error(data.message || 'Failed to add restaurant.')
			setStatus(`Restaurant created: ${data.name}`)
			setForm(prev => ({
				...prev,
				name: '',
				city: '',
				area: '',
				image: '',
				images: '',
				description: '',
				address: '',
				latitude: '',
				longitude: '',
				phone: '',
			}))
		} catch (err) {
			setStatus(err.message)
		} finally {
			setSaving(false)
		}
	}

	return (
		<section className="rounded-3xl border border-orange-100 bg-white/80 p-6 shadow-sm">
			<h2 className="title-font text-xl font-bold text-gray-900">Add Restaurant</h2>
			<form onSubmit={handleSubmit} className="mt-4 space-y-3">
				<input value={form.name} onChange={e => updateField('name', e.target.value)} required placeholder="Restaurant name" className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />
				<div className="grid gap-3 sm:grid-cols-2">
					<input value={form.city} onChange={e => updateField('city', e.target.value)} required placeholder="City" className="rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />
					<input value={form.area} onChange={e => updateField('area', e.target.value)} required placeholder="Area" className="rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />
				</div>

				<div className="rounded-2xl border border-orange-100 bg-orange-50/40 p-3">
					<p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Upload Restaurant Image To Blob</p>
					<label className="mt-2 inline-flex cursor-pointer items-center rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black">
						<input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
						{uploading ? 'Uploading...' : 'Choose Image'}
					</label>
					{form.image && <p className="mt-2 truncate text-xs text-gray-600">{form.image}</p>}
				</div>

				<input value={form.images} onChange={e => updateField('images', e.target.value)} placeholder="More image URLs (comma separated)" className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />
				<textarea value={form.description} onChange={e => updateField('description', e.target.value)} placeholder="Description" rows={3} className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />
				<input value={form.address} onChange={e => updateField('address', e.target.value)} placeholder="Address" className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />

				<div className="grid gap-3 sm:grid-cols-2">
					<input value={form.latitude} onChange={e => updateField('latitude', e.target.value)} type="number" step="any" placeholder="Latitude" className="rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />
					<input value={form.longitude} onChange={e => updateField('longitude', e.target.value)} type="number" step="any" placeholder="Longitude" className="rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />
				</div>
				<input value={form.phone} onChange={e => updateField('phone', e.target.value)} placeholder="Phone" className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />

				<div className="grid gap-3 sm:grid-cols-2">
					<input value={form.cuisine} onChange={e => updateField('cuisine', e.target.value)} placeholder="Cuisine tags (comma separated)" className="rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />
					<input value={form.tags} onChange={e => updateField('tags', e.target.value)} placeholder="Search tags (comma separated)" className="rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />
				</div>

				<input value={form.highlights} onChange={e => updateField('highlights', e.target.value)} placeholder="Highlights (comma separated)" className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />

				<div className="grid gap-3 sm:grid-cols-2">
					<input value={form.priceLevel} onChange={e => updateField('priceLevel', e.target.value)} placeholder="Price level" className="rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />
					<input value={form.distanceKm} onChange={e => updateField('distanceKm', e.target.value)} type="number" step="0.1" placeholder="Distance km" className="rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />
				</div>

				<div className="grid gap-3 sm:grid-cols-2">
					<input value={form.openingHours} onChange={e => updateField('openingHours', e.target.value)} placeholder="Opening hours" className="rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />
					<input value={form.openingStatus} onChange={e => updateField('openingStatus', e.target.value)} placeholder="Opening status text" className="rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />
				</div>

				<div className="flex flex-wrap gap-4 text-sm text-gray-700">
					<label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.isOpen} onChange={e => updateField('isOpen', e.target.checked)} />Open</label>
					<label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.delivery} onChange={e => updateField('delivery', e.target.checked)} />Delivery</label>
					<label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.takeaway} onChange={e => updateField('takeaway', e.target.checked)} />Takeaway</label>
					<label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.parking} onChange={e => updateField('parking', e.target.checked)} />Parking</label>
				</div>

				<button type="submit" disabled={saving} className="rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-5 py-2 text-sm font-semibold text-white hover:from-red-600 hover:to-orange-600 disabled:opacity-60">
					{saving ? 'Saving...' : 'Create Restaurant'}
				</button>
				{status && <p className="text-sm font-medium text-gray-700">{status}</p>}
			</form>
		</section>
	)
}
